import { linkOptions } from "@tanstack/react-router"

import {
  addDashboardRoute,
  allocationsPageRoute,
  anomalyIncidentAlertRoute,
  anomalyIncidentAlertsRoute,
  anomalyIncidentGraphsRoute,
  anomalyIncidentLogbookRoute,
  anomalyIncidentPageRoute,
  anomalyIncidentsIndexRoute,
  browserErrorsRoute,
  browserPerformanceRoute,
  checkInDetailRoute,
  checkInInstallationRoute,
  checkInLogbookRoute,
  checkInOccurrenceRoute,
  checkInOccurrencesRoute,
  checkInsIndexRoute,
  clusterNodeMetricsInspectRoute,
  clusterNodeMetricsRoute,
  clusterOverviewRoute,
  clusterPodMetricsInspectRoute,
  clusterPodMetricsRoute,
  csvExportsRoute,
  dashboardShowRoute,
  discoverRoute,
  eventGroupShowRoute,
  eventsIndexRoute,
  exceptionIncidentAttributesRoute,
  exceptionIncidentGraphsRoute,
  exceptionIncidentLogbookRoute,
  exceptionIncidentRoute,
  exceptionIncidentsGraphsRoute,
  exceptionIncidentsRoute,
  exceptionIncidentsViewRoute,
  exceptionIncidentTraceRoute,
  exceptionIncidentTracesRoute,
  exceptionIncidentTraceTimestampRoute,
  hostMetricsIndexRoute,
  hostMetricsInspectRoute,
  hostUsageIndexRoute,
  hostUsageInspectRoute,
  logLineActionsRoute,
  logsIncidentGraphsRoute,
  logsIncidentLinesRoute,
  logsIncidentLogbookRoute,
  logsIncidentRoute,
  logsIncidentsRoute,
  logsIndexRoute,
  logsTriggersRoute,
  logsViewsRoute,
  markerShowRoute,
  markersIndexRoute,
  metricsShowRoute,
  newSiteBrowserRoute,
  newSiteBrowserStepRoute,
  newSiteFrameworkRoute,
  newSiteFrameworkStepRoute,
  newSiteJsRoute,
  newSiteJsStepRoute,
  newSiteLanguageRoute,
  newSiteLanguageStepRoute,
  newSiteManualRoute,
  newSiteProvisioningRoute,
  newSiteRoute,
  performanceApdexRoute,
  performanceIncidentGraphsRoute,
  performanceIncidentLogbookRoute,
  performanceIncidentRoute,
  performanceIncidentSampleRoute,
  performanceIncidentSamplesRoute,
  performanceIncidentsGraphsRoute,
  performanceIncidentsRoute,
  performanceTracingActionRoute,
  performanceTracingActionsRoute,
  performanceTracingGraphsRoute,
  performanceTracingLogbookRoute,
  performanceTracingTraceRoute,
  performanceTracingTracesRoute,
  showOrganizationRoute,
  siteCreateRoute,
  slowEventRoute,
  slowEventsRoute,
  sourceRoute,
  sourcesRoute,
  statusPageRoute,
  statusPagesRoute,
  triggersIndexRoute,
  uptimeMonitorRoute,
  uptimeMonitorsRoute,
} from "./routes"

export type AppRouteParams = {
  organizationSlug: string
  appId: string
}

export type DashboardParams = AppRouteParams & {
  id: string
}

export type LogIncidentParams = AppRouteParams & {
  number: string
}

export type IncidentParams = LogIncidentParams

export type PerformanceTracingParams = AppRouteParams & {
  namespace: string
  action: string
}

export type CheckInParams = AppRouteParams & {
  triggerId: string
}

export type CheckInOccurrenceParams = CheckInParams & {
  id: string
}

export type AnomalyIncidentParams = AppRouteParams & {
  number: string
}

export type AnomalyIncidentAlertParams = AnomalyIncidentParams & {
  id: string
}

export const anomalyIncidentLinks = {
  index: (params: AppRouteParams) =>
    linkOptions({
      to: anomalyIncidentsIndexRoute.to,
      params,
    }),
  summary: (params: AnomalyIncidentParams) =>
    linkOptions({
      to: anomalyIncidentPageRoute.to,
      params,
    }),
  alerts: (params: AnomalyIncidentParams) =>
    linkOptions({
      to: anomalyIncidentAlertsRoute.to,
      params,
    }),
  alert: (params: AnomalyIncidentAlertParams) =>
    linkOptions({
      to: anomalyIncidentAlertRoute.to,
      params,
    }),
  graphs: (params: AnomalyIncidentParams) =>
    linkOptions({
      to: anomalyIncidentGraphsRoute.to,
      params,
    }),
  logbook: (params: AnomalyIncidentParams) =>
    linkOptions({
      to: anomalyIncidentLogbookRoute.to,
      params,
    }),
}

export const dashboardLinks = {
  index: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: dashboardShowRoute.to, params, ...(search && { search }) }),
  show: (params: DashboardParams, search?: Record<string, unknown>) =>
    linkOptions({ to: metricsShowRoute.to, params, ...(search && { search }) }),
}

export const appLinks = {
  addDashboard: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: addDashboardRoute.to, params, ...(search && { search }) }),
  allocations: (params: AppRouteParams) => linkOptions({ to: allocationsPageRoute.to, params }),
  events: (params: AppRouteParams) => linkOptions({ to: eventsIndexRoute.to, params }),
  eventGroup: (params: AppRouteParams & { group: string }, search?: Record<string, unknown>) =>
    linkOptions({ to: eventGroupShowRoute.to, params, ...(search && { search }) }),
  slowEvents: (params: AppRouteParams & { group: string }, search?: Record<string, unknown>) =>
    linkOptions({ to: slowEventsRoute.to, params, ...(search && { search }) }),
  slowEvent: (
    params: AppRouteParams & { group: string; id: string },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: slowEventRoute.to, params, ...(search && { search }) }),
  uptimeMonitors: (params: AppRouteParams) => linkOptions({ to: uptimeMonitorsRoute.to, params }),
  uptimeMonitor: (params: AppRouteParams & { id: string }) =>
    linkOptions({ to: uptimeMonitorRoute.to, params }),
  cluster: (params: AppRouteParams) => linkOptions({ to: clusterOverviewRoute.to, params }),
  clusterPods: (params: AppRouteParams) => linkOptions({ to: clusterPodMetricsRoute.to, params }),
  clusterNodes: (params: AppRouteParams) => linkOptions({ to: clusterNodeMetricsRoute.to, params }),
  clusterPodsInspect: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({
      to: clusterPodMetricsInspectRoute.to,
      params,
      ...(search && { search }),
    }),
  clusterNodesInspect: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({
      to: clusterNodeMetricsInspectRoute.to,
      params,
      ...(search && { search }),
    }),
  hostMetrics: (params: AppRouteParams) => linkOptions({ to: hostMetricsIndexRoute.to, params }),
  hostUsage: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: hostUsageIndexRoute.to, params, ...(search && { search }) }),
  hostMetricsInspect: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: hostMetricsInspectRoute.to, params, ...(search && { search }) }),
  hostUsageInspect: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: hostUsageInspectRoute.to, params, ...(search && { search }) }),
  markers: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: markersIndexRoute.to, params, ...(search && { search }) }),
  marker: (params: AppRouteParams & { revision: string; namespace: string }) =>
    linkOptions({ to: markerShowRoute.to, params }),
  triggers: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: triggersIndexRoute.to, params, ...(search && { search }) }),
}

export const organizationLinks = {
  discover: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: discoverRoute.to, params }),
  index: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: showOrganizationRoute.to, params }),
  statusPages: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: statusPagesRoute.to, params }),
  statusPage: (params: Pick<AppRouteParams, "organizationSlug"> & { id: string }) =>
    linkOptions({ to: statusPageRoute.to, params }),
  exports: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: csvExportsRoute.to, params }),
  newSite: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: newSiteRoute.to, params }),
  newSiteCreate: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: siteCreateRoute.to, params }),
  newSiteManual: (
    params: Pick<AppRouteParams, "organizationSlug">,
    search?: Record<string, unknown>,
  ) => linkOptions({ to: newSiteManualRoute.to, params, ...(search && { search }) }),
  newSiteProvisioning: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: newSiteProvisioningRoute.to, params }),
  newSiteJs: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: newSiteJsRoute.to, params }),
  newSiteBrowser: (params: Pick<AppRouteParams, "organizationSlug">) =>
    linkOptions({ to: newSiteBrowserRoute.to, params }),
  newSiteJsStep: (
    params: Pick<AppRouteParams, "organizationSlug"> & { step: string },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: newSiteJsStepRoute.to, params, ...(search && { search }) }),
  newSiteLanguage: (
    params: Pick<AppRouteParams, "organizationSlug"> & { language: string },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: newSiteLanguageRoute.to, params, ...(search && { search }) }),
  newSiteLanguageStep: (
    params: Pick<AppRouteParams, "organizationSlug"> & {
      language: string
      languageStep: string
    },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: newSiteLanguageStepRoute.to, params, ...(search && { search }) }),
  newSiteFramework: (
    params: Pick<AppRouteParams, "organizationSlug"> & {
      language: string
      languageStep: string
      framework: string
    },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: newSiteFrameworkRoute.to, params, ...(search && { search }) }),
  newSiteFrameworkStep: (
    params: Pick<AppRouteParams, "organizationSlug"> & {
      language: string
      languageStep: string
      framework: string
      frameworkStep: string
    },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: newSiteFrameworkStepRoute.to, params, ...(search && { search }) }),
  newSiteBrowserStep: (
    params: Pick<AppRouteParams, "organizationSlug"> & { step: string },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: newSiteBrowserStepRoute.to, params, ...(search && { search }) }),
}

export const logsLinks = {
  index: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: logsIndexRoute.to, params, ...(search && { search }) }),
  incidents: (params: AppRouteParams) => linkOptions({ to: logsIncidentsRoute.to, params }),
  incident: (params: LogIncidentParams) => linkOptions({ to: logsIncidentRoute.to, params }),
  incidentLines: (params: LogIncidentParams) =>
    linkOptions({ to: logsIncidentLinesRoute.to, params }),
  incidentGraphs: (params: LogIncidentParams) =>
    linkOptions({ to: logsIncidentGraphsRoute.to, params }),
  incidentLogbook: (params: LogIncidentParams) =>
    linkOptions({ to: logsIncidentLogbookRoute.to, params }),
  triggers: (params: AppRouteParams) => linkOptions({ to: logsTriggersRoute.to, params }),
  actions: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: logLineActionsRoute.to, params, ...(search && { search }) }),
  source: (params: AppRouteParams & { logSourceId: string }) =>
    linkOptions({ to: sourceRoute.to, params }),
  sources: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: sourcesRoute.to, params, ...(search && { search }) }),
  // Generates logs/views/$viewId going forward. logsViewRoute (logs/$viewId)
  // stays registered so existing bookmarks/shared links keep resolving.
  view: (params: AppRouteParams & { viewId: string }, search: Record<string, unknown>) =>
    linkOptions({ to: logsViewsRoute.to, params, search }),
}

export const browserLinks = {
  errors: (params: AppRouteParams) => linkOptions({ to: browserErrorsRoute.to, params }),
  performance: (params: AppRouteParams) => linkOptions({ to: browserPerformanceRoute.to, params }),
}

export const checkInLinks = {
  index: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: checkInsIndexRoute.to, params, ...(search && { search }) }),
  detail: (params: CheckInParams) => linkOptions({ to: checkInDetailRoute.to, params }),
  occurrences: (params: CheckInParams, search?: Record<string, unknown>) =>
    linkOptions({ to: checkInOccurrencesRoute.to, params, ...(search && { search }) }),
  occurrence: (params: CheckInOccurrenceParams) =>
    linkOptions({ to: checkInOccurrenceRoute.to, params }),
  logbook: (params: CheckInParams) => linkOptions({ to: checkInLogbookRoute.to, params }),
  installation: (params: CheckInParams) => linkOptions({ to: checkInInstallationRoute.to, params }),
}

export const exceptionLinks = {
  index: (params: AppRouteParams, search?: Record<string, unknown>) =>
    linkOptions({ to: exceptionIncidentsRoute.to, params, ...(search && { search }) }),
  graphs: (params: AppRouteParams) => linkOptions({ to: exceptionIncidentsGraphsRoute.to, params }),
  view: (params: AppRouteParams & { viewId: string }, search: Record<string, unknown>) =>
    linkOptions({ to: exceptionIncidentsViewRoute.to, params, search }),
  summary: (params: IncidentParams) => linkOptions({ to: exceptionIncidentRoute.to, params }),
  traces: (params: IncidentParams, search?: Record<string, unknown>) =>
    linkOptions({ to: exceptionIncidentTracesRoute.to, params, ...(search && { search }) }),
  attributes: (params: IncidentParams, search?: Record<string, unknown>) =>
    linkOptions({ to: exceptionIncidentAttributesRoute.to, params, ...(search && { search }) }),
  incidentGraphs: (params: IncidentParams) =>
    linkOptions({ to: exceptionIncidentGraphsRoute.to, params }),
  logbook: (params: IncidentParams) =>
    linkOptions({ to: exceptionIncidentLogbookRoute.to, params }),
  trace: (params: IncidentParams & { traceId: string }, search?: Record<string, unknown>) =>
    linkOptions({ to: exceptionIncidentTraceRoute.to, params, ...(search && { search }) }),
  traceTimestamp: (params: IncidentParams & { timestamp: string }) =>
    linkOptions({ to: exceptionIncidentTraceTimestampRoute.to, params }),
}

export const performanceLinks = {
  index: (params: AppRouteParams) => linkOptions({ to: performanceIncidentsRoute.to, params }),
  graphs: (params: AppRouteParams) =>
    linkOptions({ to: performanceIncidentsGraphsRoute.to, params }),
  apdex: (params: AppRouteParams) => linkOptions({ to: performanceApdexRoute.to, params }),
  summary: (params: IncidentParams) => linkOptions({ to: performanceIncidentRoute.to, params }),
  samples: (params: IncidentParams, search?: Record<string, unknown>) =>
    linkOptions({ to: performanceIncidentSamplesRoute.to, params, ...(search && { search }) }),
  incidentGraphs: (params: IncidentParams) =>
    linkOptions({ to: performanceIncidentGraphsRoute.to, params }),
  logbook: (params: IncidentParams) =>
    linkOptions({ to: performanceIncidentLogbookRoute.to, params }),
  sample: (params: IncidentParams & { sampleId: string }, search?: Record<string, unknown>) =>
    linkOptions({ to: performanceIncidentSampleRoute.to, params, ...(search && { search }) }),
  tracingIndex: (params: AppRouteParams) =>
    linkOptions({ to: performanceTracingActionsRoute.to, params }),
  tracing: (params: PerformanceTracingParams) =>
    linkOptions({ to: performanceTracingActionRoute.to, params }),
  tracingGraphs: (params: PerformanceTracingParams) =>
    linkOptions({ to: performanceTracingGraphsRoute.to, params }),
  tracingLogbook: (params: PerformanceTracingParams) =>
    linkOptions({ to: performanceTracingLogbookRoute.to, params }),
  tracingTraces: (params: PerformanceTracingParams) =>
    linkOptions({ to: performanceTracingTracesRoute.to, params }),
  tracingTrace: (
    params: PerformanceTracingParams & { traceId: string },
    search?: Record<string, unknown>,
  ) => linkOptions({ to: performanceTracingTraceRoute.to, params, ...(search && { search }) }),
}
